import {
  validateCompanyNameText,
  validateEmailAddress,
  validateLettersSpacesCommasNameRequired,
  validateOptionalTextMaxLen,
  sanitizePhoneInput,
  type CreateBookingOption,
  type InviteCarrierLookupResult,
} from "design-components";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { CarrierInviteSubmitPayload } from "../types";

const FIELD_KEYS = ["carrierName", "contactName", "email"] as const;
type FieldKey = (typeof FIELD_KEYS)[number];

export interface UseCarrierInviteDrawerParams {
  open: boolean;
  onDotLookup?: (dotNumber: string) => Promise<InviteCarrierLookupResult>;
}

export function useCarrierInviteDrawer(params: UseCarrierInviteDrawerParams) {
  const { open, onDotLookup } = params;

  const [customer, setCustomer] = useState<CreateBookingOption | null>(null);
  const [hasFederalDot, setHasFederalDot] = useState(false);
  const [dotNumber, setDotNumber] = useState("");
  const [dotLookupLoading, setDotLookupLoading] = useState(false);
  const [lookupDetails, setLookupDetails] =
    useState<InviteCarrierLookupResult | null>(null);
  const [carrierName, setCarrierName] = useState("");
  const [contactName, setContactName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [scac, setScac] = useState("");
  const [stateProvincialId, setStateProvincialId] = useState("");
  const [businessAddress, setBusinessAddress] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const clearCarrierDetailFields = useCallback(() => {
    setCarrierName("");
    setContactName("");
    setEmail("");
    setPhone("");
    setScac("");
    setStateProvincialId("");
    setBusinessAddress("");
    setFieldErrors((prev) => {
      const next = { ...prev };
      for (const key of [
        ...FIELD_KEYS,
        "phone",
        "scac",
        "stateProvincialId",
        "businessAddress",
      ]) {
        delete next[key];
      }
      return next;
    });
  }, []);

  const showCarrierDetailsFields = !hasFederalDot || lookupDetails != null;

  const resetForm = useCallback(() => {
    setCustomer(null);
    setHasFederalDot(false);
    setDotNumber("");
    setDotLookupLoading(false);
    setLookupDetails(null);
    setCarrierName("");
    setContactName("");
    setEmail("");
    setPhone("");
    setScac("");
    setStateProvincialId("");
    setBusinessAddress("");
    setFieldErrors({});
  }, []);

  useEffect(() => {
    if (open) {
      resetForm();
    }
  }, [open, resetForm]);

  const fieldErrorFor = useCallback(
    (field: FieldKey): string | undefined => {
      switch (field) {
        case "carrierName":
          return validateCompanyNameText(carrierName);
        case "contactName":
          return validateLettersSpacesCommasNameRequired(contactName);
        case "email":
          return validateEmailAddress(email, {
            required: true,
            minLen: 3,
            maxLen: 50,
          });
        default:
          return undefined;
      }
    },
    [carrierName, contactName, email],
  );

  const setFieldError = useCallback(
    (field: string, message: string | undefined) => {
      setFieldErrors((prev) => {
        const next = { ...prev };
        if (message) next[field] = message;
        else delete next[field];
        return next;
      });
    },
    [],
  );

  const validateFieldOnBlur = useCallback(
    (field: FieldKey) => {
      setFieldError(field, fieldErrorFor(field));
    },
    [fieldErrorFor, setFieldError],
  );

  const handleCustomerChange = useCallback(
    (_nextValue: string | null, nextOption: CreateBookingOption | null) => {
      setCustomer(nextOption);
      if (nextOption) {
        setFieldError("customerId", undefined);
      }
    },
    [setFieldError],
  );

  const handleCustomerBlur = useCallback(() => {
    if (!customer) {
      setFieldError("customerId", "Select a customer to continue.");
    }
  }, [customer, setFieldError]);

  /** FR-AV166-011/012 — silent validity for Invite button enablement (no fieldErrors writes). */
  const isFormValid = useMemo((): boolean => {
    if (!customer?.value?.trim()) return false;
    if (hasFederalDot) {
      if (!dotNumber.trim() || !lookupDetails) return false;
    }
    if (!showCarrierDetailsFields) return false;
    for (const key of FIELD_KEYS) {
      if (fieldErrorFor(key)) return false;
    }
    if (validateOptionalTextMaxLen(scac, 20)) return false;
    if (!hasFederalDot && validateOptionalTextMaxLen(stateProvincialId, 50)) {
      return false;
    }
    if (validateOptionalTextMaxLen(businessAddress, 100)) return false;
    return true;
  }, [
    businessAddress,
    customer,
    dotNumber,
    fieldErrorFor,
    hasFederalDot,
    lookupDetails,
    scac,
    showCarrierDetailsFields,
    stateProvincialId,
  ]);

  const validate = useCallback((): boolean => {
    const errs: Record<string, string> = {};
    if (!customer?.value?.trim()) {
      errs.customerId = "Select a customer to continue.";
    }
    if (hasFederalDot && !dotNumber.trim()) {
      errs.dotNumber = "Enter a DOT number.";
    } else if (hasFederalDot && !lookupDetails) {
      errs.dotNumber = "Search by DOT number to load carrier details.";
    }
    if (!showCarrierDetailsFields) {
      setFieldErrors(errs);
      return Object.keys(errs).length === 0;
    }
    for (const key of FIELD_KEYS) {
      const msg = fieldErrorFor(key);
      if (msg) errs[key] = msg;
    }
    const scacErr = validateOptionalTextMaxLen(scac, 20);
    if (scacErr) errs.scac = scacErr;
    if (!hasFederalDot) {
      const stateErr = validateOptionalTextMaxLen(stateProvincialId, 50);
      if (stateErr) errs.stateProvincialId = stateErr;
    }
    const addressErr = validateOptionalTextMaxLen(businessAddress, 100);
    if (addressErr) errs.businessAddress = addressErr;
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  }, [
    businessAddress,
    customer,
    dotNumber,
    fieldErrorFor,
    hasFederalDot,
    lookupDetails,
    scac,
    showCarrierDetailsFields,
    stateProvincialId,
  ]);

  const buildPayload = useCallback(
    (): CarrierInviteSubmitPayload => ({
      customerId: customer?.value?.trim() ?? "",
      carrierName: carrierName.trim(),
      contactName: contactName.trim(),
      email: email.trim(),
      phone: phone.trim(),
      scac: scac.trim(),
      stateProvincialId: hasFederalDot ? "" : stateProvincialId.trim(),
      businessAddress: businessAddress.trim(),
      dotNumber: hasFederalDot ? dotNumber.trim() : "",
      ifCarrierHasFederalDot: hasFederalDot,
      dotStatus: lookupDetails?.dotStatus.trim() ?? "",
      riskScore: lookupDetails?.riskScore.trim() ?? "",
      safetyScore: lookupDetails?.safetyScore.trim() ?? "",
    }),
    [
      businessAddress,
      carrierName,
      contactName,
      customer,
      dotNumber,
      email,
      hasFederalDot,
      lookupDetails,
      phone,
      scac,
      stateProvincialId,
    ],
  );

  const runDotLookup = useCallback(
    async (dot: string) => {
      if (!onDotLookup) {
        setFieldError("dotNumber", "DOT lookup is not available.");
        return;
      }
      setFieldError("dotNumber", undefined);
      setLookupDetails(null);
      clearCarrierDetailFields();
      setDotLookupLoading(true);
      try {
        const result = await onDotLookup(dot.trim());
        setLookupDetails(result);
        setCarrierName(result.carrierName.trim());
        setContactName(result.contactName);
        setEmail(result.email);
        setBusinessAddress(result.businessAddress);
      } catch (err) {
        setLookupDetails(null);
        clearCarrierDetailFields();
        const msg =
          err instanceof Error && err.message.trim()
            ? err.message.trim()
            : "Could not look up carrier by DOT. Try again.";
        setFieldError("dotNumber", msg);
      } finally {
        setDotLookupLoading(false);
      }
    },
    [clearCarrierDetailFields, onDotLookup, setFieldError],
  );

  const handleDotNumberChange = useCallback(
    (nextDot: string) => {
      setDotNumber(nextDot);
      setFieldError("dotNumber", undefined);
      if (
        lookupDetails != null ||
        carrierName ||
        contactName ||
        email ||
        phone ||
        scac ||
        businessAddress
      ) {
        setLookupDetails(null);
        clearCarrierDetailFields();
      }
    },
    [
      businessAddress,
      carrierName,
      clearCarrierDetailFields,
      contactName,
      email,
      lookupDetails,
      phone,
      scac,
      setFieldError,
    ],
  );

  const handleFederalDotToggle = useCallback(
    (checked: boolean) => {
      setHasFederalDot(checked);
      setFieldError("dotNumber", undefined);
      setLookupDetails(null);
      setDotNumber("");
      if (!checked) {
        setFieldError("customerId", undefined);
      }
      clearCarrierDetailFields();
    },
    [clearCarrierDetailFields, setFieldError],
  );

  const handlePhoneChange = useCallback((raw: string) => {
    setPhone(sanitizePhoneInput(raw));
  }, []);

  return {
    customerValue: customer?.value ?? null,
    handleCustomerChange,
    handleCustomerBlur,
    hasFederalDot,
    handleFederalDotToggle,
    showCarrierDetailsFields,
    dotNumber,
    handleDotNumberChange,
    dotLookupLoading,
    lookupDetails,
    runDotLookup,
    carrierName,
    setCarrierName,
    contactName,
    setContactName,
    email,
    setEmail,
    phone,
    handlePhoneChange,
    scac,
    setScac,
    stateProvincialId,
    setStateProvincialId,
    businessAddress,
    setBusinessAddress,
    fieldErrors,
    setFieldError,
    validateFieldOnBlur,
    validate,
    isFormValid,
    buildPayload,
  };
}
